import type {
  CoachingNote,
  PlayerGoal,
  ReferenceVideoProvider,
} from "@/lib/types";

/**
 * Reference-video helpers (migration 0053, issue #675).
 *
 * **The rule this module holds: nothing reaches the video provider until
 * the viewer presses play.**
 *
 * The poster comes from `thumb_url` on our own domain, and the embed
 * `<iframe>` is mounted only in response to an explicit click. That is the
 * mechanism behind the promise written into
 * `docs/legal/beta/beta-privacy-notice.md` and the parental consent form:
 * a family who never presses play is never disclosed to Google or Vimeo.
 *
 * Two ways a well-meaning change could break it silently, both guarded by
 * `tests/e2e/reference-video-privacy.spec.js`:
 *
 *   - Pointing the poster `<img>` at the provider's CDN. Looks like a
 *     simplification; contacts Google on every card render.
 *   - Rendering the `<iframe>` unconditionally with `autoplay=0`. Looks
 *     inert; still loads the provider's player and ships the viewer's IP.
 *
 * The embed URL is built from the stored `(provider, video_id)` pair, which
 * the server derived and validated. A coach-supplied string never reaches
 * an `iframe src` — which matters because the app ships no CSP.
 */

export type ReferenceVideoCarrier = Pick<
  CoachingNote | PlayerGoal,
  | "ref_video_provider"
  | "ref_video_id"
  | "ref_video_label"
  | "ref_video_title"
  | "ref_video_thumb"
>;

export type ReferenceVideo = {
  provider: ReferenceVideoProvider;
  videoId: string;
  label: string | null;
  title: string | null;
  /** Poster URL on OUR domain, or null when there's no stored poster. */
  thumbUrl: string | null;
  /** True when playing contacts a third party. Drives the disclosure line. */
  isThirdParty: boolean;
};

/** Human name for the provider, for the disclosure line and badges. */
export function providerName(provider: ReferenceVideoProvider): string {
  switch (provider) {
    case "youtube":
      return "YouTube";
    case "vimeo":
      return "Vimeo";
    case "file":
      return "a video file";
  }
}

/**
 * Read a reference video off a note or goal, or null when there isn't one.
 *
 * Requires BOTH provider and id — the API enforces that they travel
 * together, and a half-written pair would otherwise render a broken card.
 */
export function readReferenceVideo(
  carrier: ReferenceVideoCarrier | null | undefined,
): ReferenceVideo | null {
  if (!carrier) return null;
  const provider = carrier.ref_video_provider ?? null;
  const videoId = carrier.ref_video_id ?? null;
  if (!provider || !videoId) return null;

  const thumb = carrier.ref_video_thumb ?? null;
  return {
    provider,
    videoId,
    label: carrier.ref_video_label ?? null,
    title: carrier.ref_video_title ?? null,
    // Served by us on purpose — see the module docstring.
    thumbUrl: thumb
      ? `/api/coach/reference-video/thumb/${encodeURIComponent(thumb)}`
      : null,
    // A direct file plays in tktic's own player, so no third party is
    // involved and no disclosure is owed for it.
    isThirdParty: provider !== "file",
  };
}

/**
 * The URL to load once the viewer has pressed play.
 *
 * Built from the validated pair, never from anything a coach typed.
 * `youtube-nocookie.com` is what the privacy notice tells families we use —
 * it isn't a substitute for click-to-load (it still sets storage once
 * playing), but it is the domain we committed to.
 */
export function embedUrl(video: ReferenceVideo): string {
  switch (video.provider) {
    case "youtube":
      return `https://www.youtube-nocookie.com/embed/${video.videoId}?autoplay=1&rel=0`;
    case "vimeo":
      return `https://player.vimeo.com/video/${video.videoId}?autoplay=1`;
    case "file":
      // The id IS the server-validated https URL for a direct file.
      return video.videoId;
  }
}

/** Where "open it at the source" should point when an embed fails. */
export function watchUrl(video: ReferenceVideo): string {
  switch (video.provider) {
    case "youtube":
      return `https://www.youtube.com/watch?v=${video.videoId}`;
    case "vimeo":
      return `https://vimeo.com/${video.videoId}`;
    case "file":
      return video.videoId;
  }
}

/**
 * The line shown under the player before play.
 *
 * Null for a direct file: there is nobody to disclose, and a reassurance
 * about a third party that isn't involved would be noise at best and
 * misleading at worst.
 */
export function disclosureText(video: ReferenceVideo): string | null {
  if (!video.isThirdParty) return null;
  return `Plays from ${providerName(
    video.provider,
  )}. Nothing is sent to them until you press play.`;
}
